// exports Point, default: Point

import { Base, rad2deg } from 'gear';
import { Xml } from '../util';

const _Math  = Math,
      _abs   = _Math.abs,
      _atan2 = _Math.atan2,
      _pow   = _Math.pow,
      _sqrt  = _Math.sqrt,
      _Math_PI = _Math.PI,
      _Math_2_PI = 2*_Math_PI,
      _parseFloat = parseFloat;

export const Point = function(x,y) {
  const _this = this;
  if( ! ( _this instanceof Point )) {
    console.log('[Point] missing new');
    return new Point(x,y);
  }
  if( y===undefined ) {
    console.log('[Point] undefined y');
    _this.x = x.x;
    _this.y = x.y;
  }
  else {
    _this.x = x;
    _this.y = y;
  }
};

Base.Type['Point'] = Point;

export default Point;

const _toFixed = function(x) {
  return (+x.toFixed(4));
};

const _textualize = function() {
  return _toFixed(this.x) + ' ' + _toFixed(this.y);
};

const _clone = function() {
  return new Point(this.x,this.y);
};

const _scalarMultiply = function(alpha) {
  this.x *= alpha;
  this.y *= alpha;
  return this;
};

Point.prototype = {
  constructor: Point,
  type: 'Point',

  get center() {
    return this.clone();
  },
  set center( c ) {
    this.set(c);
  },

  sm: _scalarMultiply, // shortcut for scalarMultiply
  scalarMultiply: _scalarMultiply,
  add( x, y ) {
    if( y !== undefined ){
      console.log('[Point] add, x,y');
    }
    if( y === undefined ) { y = x.y; x = x.x; }
    this.x += x;
    this.y += y;
    return this;
  },
  add_( x, y ) {
    this.x += x;
    this.y += y;
    return this;
  },

  substract( x, y ) {
    if( y !== undefined ){
      console.log('[Point] substract, x,y');
    }

    if( y === undefined ) { y = x.y; x = x.x; }
    this.x -= x;
    this.y -= y;
    return this;
  },
  substract_( x, y ) {
    this.x -= x;
    this.y -= y;
    return this;
  },

  plus_( x, y ) {
    return new Point( this.x + x, this.y + y );
  },
  plus( point ) {
    return new Point( this.x + point.x, this.y + point.y );
  },
  minus_( x, y ) {
    return new Point( this.x - x, this.y - y );
  },
  minus( point ) {
    return new Point( this.x - point.x, this.y - point.y );
  },
  scaled( alpha ) {
    return new Point( this.x * alpha, this.y * alpha );
  },

  copy : _clone,
  clone: _clone,

  isEqual( other ) {
    const eps = 0.001;
    return _abs(this.x-other.x) < eps &&
           _abs(this.y-other.y) < eps;
  },

  isNear( other, eps = 0.001 ) {
    return _abs(this.x-other.x) <= eps &&
           _abs(this.y-other.y) <= eps;
  },

  translate_( dx, dy ) { // Alias to add_
    this.x += dx;
    this.y += dy;
    return this;
  },
  translated_( dx, dy ) { // Alias to plus_
    return new Point( this.x+dx, this.y+dy );
  },

  translate( dx, dy ) {
    if( dy !== undefined ){
      console.log('[Point] translate, x,y');
    }
    if( dy === undefined ) { dy = dx.y; dx = dx.x; }

    this.x+=dx; this.y+=dy;
    return this;
  },
  translated( dx, dy ) {
    return this.clone().translate(dx,dy);
  },

  transform( t ) {
    const np = t.apply(this);
    this.x = np.x;
    this.y = np.y;
    return this;
  },
  transformed( t ) {
    return t.apply(this);
  },

  toSVG: _textualize,
  toXmlString: _textualize,
  textualize: _textualize,

  norm2() {
    return this.x * this.x + this.y * this.y;
  },
  norm() {
    return _sqrt(this.x * this.x + this.y * this.y);
  },
  normalized() {
    return Point.sm(this,1/this.norm());
  },
  normalize() {
    this.scalarMultiply(1 / this.norm());
    return this;
  },

  perpendicular( side ) {
    const m = side < 0 ? 1: -1;
    return new Point(this.y*m,-this.x*m);
  },

  isZero() {
    return this.x === 0 && this.y === 0;
  },

  confine( lx, ly, hx, hy ) {
    if( ly === undefined ) {
      const bounds = lx;
      lx = bounds.lx;
      ly = bounds.ly;
      hx = bounds.hx;
      hy = bounds.hy;
    }
    const _this = this;
    if( _this.x < lx) { _this.x = lx; }
    if( _this.x > hx) { _this.x = hx; }
    if( _this.y < ly) { _this.y = ly; }
    if( _this.y > hy) { _this.y = hy; }
    return _this;
  },
  confined( lx, ly, hx, hy ) {
    return this.clone().confine(lx,ly,hx,hy);
  },

  angle() {
    return rad2deg( _atan2(this.y,this.x) );
  },
  angle_rad() {
    return _atan2(this.y,this.x);
  },

  set( x, y ) {
    if( y !== undefined ) {
      console.log('[Point] set, x,y');
    }
    if( y === undefined ) { y = x.y; x = x.x; }
    this.x = x;
    this.y = y;
    return this;
  },
  set_( x, y ) {
    this.x = x;
    this.y = y;
    return this;
  },

  distanceToLine2( from, to ) {
    const q = this.projectToLine( from, to );
    return Point.distance2(this,q);
  },

  projectToLine( from, to ) {
    const v = Point.substract(to,from);
    const segLength = v.norm();
    if( segLength !== 0 ) {
      const dir = Point.sm( v, 1/segLength );
      const l = Point.dotProduct(dir, Point.substract(this,from) );
      const u = l/segLength;
      return Point.sm(from,1-u).add( Point.sm(to,u) );
    }
    else {
      return from;
    }
  },

  // Returns the parameter corresponding to the orthogonal projection of
  // this point on the line supported by the vector pa->pb. In order to avoid
  // square roots, the returned parameter is multiplied by the length of the
  // vector. The optional parameter len2 is the squared length of the vector
  // pa->pb.
  projectionOnLineParameter(pa,pb,len2){
    var vec=Point.substract(pb,pa); // vector supporting the line
    if(len2 === undefined){
      len2=vec.norm2();
    }
    // If the squared length of the vector is not zero:
    if(len2 !== 0){
      var vecDir=Point.substract(this,pa); // vector pa->this
      // Return scalar product of the vectors, times the length of pa->pb (if
      // vec was normalized, the result of the following operation would be just
      // the parameter).
      return vec.x*vecDir.x+vec.y*vecDir.y;
    }else{
      return 0.0;
    }
  },

  // Given a segment pa-pb, returns the segments closest point to this.
  getProjectionToSeg(pa,pb){
    var vec=Point.substract(pb,pa); // vector pa->pb
    var len2=vec.norm2(); // squared length of the segment pa-pb
    if(len2===0.0){ // if the length of the segment is zero, return an endpoint
      return pa;
    }
    var paramOnLine=this.projectionOnLineParameter(pa,pb,len2);
    // If paramOnLine is smaller than zero or bigger than the length, then
    // the projection on the line falls outside the segment. In this case,
    // we return one of the endpoints.
    if(paramOnLine<=0.0){
      return pa;
    }
    if(paramOnLine>=len2){
      // If the parameter on the line times |vec| is bigger or equal to |vec|^2,
      // this means that the parameter is >= than |vec|.
      return pb;
    }
    // If the parameter is bigger than zero and smaller than the segment length,
    // then we return the projection of the point on the line, which falls
    // inside the segment.
    return Point.interpolate(pa,pb,paramOnLine/len2);
  }

};

Point.areEqual = function( a, b ) {
  return a.x === b.x && a.y === b.y;
};

Point.fromXmlString = function( cdlPoint ) {
  const xy = cdlPoint.split(" ");
  return new Point( _parseFloat(xy[0]), _parseFloat(xy[1]) );
};

Point.distance = function( a, b ) { // do not reuse distance2 for performance
  const dx = (a.x-b.x), dy = (a.y-b.y);
  return _sqrt(dx*dx+dy*dy);
};

Point.distance2 = function( a, b ) {
  const dx = (a.x-b.x), dy = (a.y-b.y);
  return dx*dx+dy*dy;
};

// Returns: w = minmun angle from a to b, in (-PI,PI].
// w is the smallest angle you need to ccw-rotate a to reach aB
// (if w &lt; 0 the rotaton is actually cw)
// w = 0 if a==b.
// PRECONDITION: ( b.x != 0 || b.y != 0 ) && ( a.x != 0 || a.y != 0 )
// Returns: A value in radians over (-PI,+PI]
const Point_turnAngle_rad = Point.turnAngle_rad = function( a, b ) {
  const angle01 = a.angle_rad(),
        angle12 = b.angle_rad(),
        sweep = angle12 - angle01;
  return (sweep < -_Math_PI ? sweep + _Math_2_PI : (sweep > _Math_PI ? sweep - _Math_2_PI : sweep));
};

Point.turnAngle = function( a, b ) {
  return rad2deg(Point_turnAngle_rad(a,b));
};

Point.minus = function( p ) {
  return new Point( -p.x, -p.y );
};

Point.add = function( pa, pb ) {
  return new Point( pa.x + pb.x, pa.y + pb.y);
};

const Point_substract = Point.substract = function( pa, pb ) {
  return new Point(pa.x - pb.x, pa.y - pb.y);
};

Point.sm = Point.scalarMultiply = function( p, alpha ) {
  return new Point(p.x * alpha, p.y * alpha);
};

Point.dotProduct = function( p1, p2 ) {
  return p1.x*p2.x+p1.y*p2.y;
};

const Point_crossProductZ = Point.crossProductZ = function( p1, p2 ) {
  return p1.x*p2.y-p1.y*p2.x;
};

Point.normalize = function( p ) {
  return Point.sm(p,1/p.norm());
};

Point.FromBezier = function( percent, C1, C2, C3, C4 ) {
  const t = 1 - percent;
  const b1 = t * t * t,
      b2 = 3 * t * t * (1 - t),
      b3 = 3 * t * (1 - t) * (1 - t),
      b4 = (1 - t) * (1 - t) * (1 - t);
  return new Point( C1.x * b1 + C2.x * b2 + C3.x * b3 + C4.x * b4 ,
                 C1.y * b1 + C2.y * b2 + C3.y * b3 + C4.y * b4 );
};

Point.FromPolygon = function( percent, pa, pb ) {
  return new Point( pa.x + (pb.x - pa.x) * percent ,
                 pa.y + (pb.y - pa.y) * percent );
};

// Given two points pa and pb, and a parameter t, returns the point
// which is the result of pa+t*d, where d is the normalized direction of
// the segment (t=0 gives pa and t=length gives pb).
Point.interpolate = function( pa, pb, t ) {
  return new Point((1-t)*pa.x+t*pb.x,(1-t)*pa.y+t*pb.y);
};


// Based on: http://www.codeproject.com/Tips/84226/Is-a-Point-inside-a-Polygon
// And: http://erich.realtimerendering.com/ptinpoly/
// In addition, when the flag includeEdges is set, we handle here the
// degenerated situation on which the query point is on the boundary of the
// polygon, returning true.

Point.isInsidePolygon = function( point, poly, includeEdges = true ) {
  const l = poly.length;
  const x = point.x, y = point.y;
  let c = false;
  for( let i = 0, j = l - 1; i < l; j = i++) {
    if((poly[i].y > y) !== (poly[j].y > y)) {
      // assert( poly[i].y != poly[j].y )
      const pdy = poly[j].y - poly[i].y;
      const lc = (x - poly[i].x) * pdy;
      const rc = (poly[j].x - poly[i].x) * (y - poly[i].y);
      if((pdy > 0 && lc < rc) ||
          (pdy < 0 && lc > rc)) {
        c = !c;
      } else if ( includeEdges && lc === rc){
        // lc === rc when the point is on the edge or on a vertex. In this
        // case, we return true because the point is inside te polygon. We
        // don't consider this case inside the next one because, in case the
        // point is on a polygon vertex, the test will be true for two
        // consecutive edges, yielding an incorrect result.
        return true;
      }
    }
    const poly_i = poly[i];
    if( includeEdges && poly_i.x===x && poly_i.y===y){
      // This is true iff the point equals a vertex. We put the test outside
      // the main test because it may be the case that this is true but the
      // first test is false.
      return true;
    }
  }
  return c;
};

// Based on: http://geomalgorithms.com/a03-_inclusion.html

// orientedSide(): tests if a point is Left|On|Right of an infinite line.
//    Input:  three points P0, P1, and P2
//    Return: >0 for P2 left of the line through P0 and P1
//            =0 for P2  on the line
//            <0 for P2  right of the line
//    See: Algorithm 1 "Area of Triangles and Polygons"
Point.orientedSide = function( p0, p1, p2 ) {
  const d = ((p1.x - p0.x) * (p2.y - p0.y) - (p2.x - p0.x) * (p1.y - p0.y));
  return d > 0 ? +1 : ( d < 0 ? -1 : 0 ) ;
};

// Returns true iff p2 is to the left of the oriented side p0p1.
// A subtraction will be positive if the minuend is bigger than the subtrahend.
const Point_isLeft = Point.isLeft = function( p0, p1, p2 ) { // isSomething should return true/false
  return ((p1.x - p0.x) * (p2.y - p0.y) > (p2.x - p0.x) * (p1.y - p0.y));
};

// Returns true iff p2 is to the right of the oriented side p0p1.
const Point_isRight = Point.isRight = function( p0, p1, p2 ) {
  return ((p1.x - p0.x) * (p2.y - p0.y) < (p2.x - p0.x) * (p1.y - p0.y));
};

// Returns true iff p0, p1 and p2 are aligned.
const Point_isAligned = Point.isAligned = function( p0, p1, p2 ) {
  return ((p1.x - p0.x) * (p2.y - p0.y) === (p2.x - p0.x) * (p1.y - p0.y));
};

// Input: point is a query point, poly is a hull of points, d2 is some squared
// distance to compare. Returns true iff there is a point on an edge of poly
// whose squared distance to point is smaller than or equal to d2.  Precondition:
// the points of poly are ordered (it doesn't matter if they are CW or CCW).
Point.isNearPolygon = function(point,poly,d2){
  for(let k=0, kEnd=poly.length; k<kEnd; ++k){
    if(Point.distance2(point,point.getProjectionToSeg(poly[k],poly[(k+1)%kEnd]))
        <=d2){
      return true;
    }
  }
  return false;
};

// The winding number is not defined when the query point belongs to the
// curve. Although this function handles that case, it is only used when the
// winding number is defined.
Point.windNumber = function( point, poly ) {
  let wn = 0; // the  winding number counter

  const y = point.y;
  // loop through all edges of the polygon
  const n = poly.length - 1;
  for( let i = 0; i < n; i++) { // edge from poly[i] to poly[i+1]
    if(poly[i].y <= y) { // start py <= y
      if(poly[i + 1].y > y) {                            // an upward crossing
        if(Point_isLeft(poly[i], poly[i + 1], point)) {  // point left of edge
          ++wn; // have  a valid up intersect
        }
      }
    } else { // start py > y
      if(poly[i + 1].y <= y) {                           // a downward crossing
        if(Point_isRight(poly[i], poly[i + 1], point)) {   // P right of  edge
          --wn; // have  a valid down intersect
        }
      }
    }
  }
  return wn;
};

const _offsetForFastTranslate = function( d, scale ) {
  const s = +scale.toFixed(6);
  return Math.round(d * s) / s;
};

Point.asFastOffset = function( point, scale ) {
  return new Point(_offsetForFastTranslate(point.x, scale), _offsetForFastTranslate(point.y, scale));
};

// If some of the segments is a point, this function will not work, because
// det will be zero. Those cases would need to be handled in a different way,
// but that is not relevant here because we only need segment intersection
// functions to check box overlap.
const _computeSegmentsIntersection = function( as, at, bs, bt ) {
  const adir = Point_substract(at,as) ,
      bdir = Point_substract(bt,bs) ,
      det  = Point_crossProductZ(adir,bdir) ;
  if( det !== 0 ) {
    const d    = Point_substract(bs,as) ,
        deta = Point_crossProductZ(d, bdir) ,
        detb = Point_crossProductZ(d, adir) ,
        tta  = deta / det ,
        ttb  = detb / det ;

    return { intersect: true, ta : tta, tb : ttb } ;
  }
  else {
    return { intersect : false } ;
  }
};

// This function works in the generic case, that is, when both segments
// are not degenerted and have at most one intersection point.
Point.doSegmentsIntersect = function( as, at, bs, bt ) {
  const isec = _computeSegmentsIntersection(as,at,bs,bt);
  if( isec.intersect ) {
    return isec.ta >= 0 && isec.ta <=1 && isec.tb >= 0 && isec.tb <= 1 ;
  }
  else {
    return false ;
  }
};

Xml.Parser['Point'] = {
  decode( node, tag, v ) {
    const valueString = node.getAttribute(tag) ;
    if( valueString ) {
      return Point.fromXmlString(valueString);
    }
    return v;
  },
  encode( node, tag, v ) {
    node.setAttribute(tag,v.toXmlString());
  }
};



/** WEBPACK FOOTER **
 ** ./src/cdl/geometry/point.js
 **/